import { PLAYER_COLORS } from "@/lib/api";
import { LAYER } from "@/lib/terrain";

// TERRAIN 2 of 3: Sea Walls sit on the board's EDGES (game.walls, keyed by edge id). Each wall is
// drawn along its own edge, inside the square it was built on; a sabotaged wall keeps its red
// slash. While a Sea Wall card (or a Sabotage / Repair edge step) waits for an edge, the chosen
// square shows its 4 edge outlines: free edges are clickable, the others greyed with the reason.
export const WALL_T = 7; // wall thickness (stage px)
export const WALL_INSET = 2;
export const SIDES = ["top", "bottom", "left", "right"];
const SIDE_LABEL = { top: "Top", bottom: "Bottom", left: "Left", right: "Right" };

export function edgeId(key, side) {
  const [r, c] = key.split(",").map(Number);
  if (side === "top") return `h${r},${c}`;
  if (side === "bottom") return `h${r + 1},${c}`;
  if (side === "left") return `v${r},${c}`;
  return `v${r},${c + 1}`;
}

// Sides of `key` that carry a wall built on `key` (drawn inside this square).
export function wallSidesOf(walls, key) {
  const out = new Set();
  for (const w of Object.values(walls || {})) if (w?.square === key) out.add(w.side);
  return out;
}

const nameOf = (players, color) => players.find((p) => p.color === color)?.name || "another player";

// Same wording as the server (weather/flooding.py edge_options).
export function wallEdgeOptions(walls, key, color, players) {
  return SIDES.map((side) => {
    const w = (walls || {})[edgeId(key, side)];
    if (!w) return { side, ok: true, reason: null };
    const who = w.owner === color ? "you" : nameOf(players, w.owner);
    return { side, ok: false, reason: `Already walled by ${who}${w.sabotaged ? " (sabotaged)" : ""}` };
  });
}

export const wallText = (w, players) =>
  `Sea Wall (${nameOf(players, w.owner)}, ${w.side} edge) · the sea can't pass it · storm +1 · -1 population${w.sabotaged ? " · sabotaged: works as no wall" : ""}`;

// Wall strip along `side` of the visible top face v = {left, top, width, height} (geom.vis).
function box(side, v) {
  const { left: x, top: y, width: w, height: h } = v;
  if (side === "top") return { left: x + WALL_INSET + 1, top: y + WALL_INSET, width: w - 2 * WALL_INSET - 2, height: WALL_T };
  if (side === "bottom") return { left: x + WALL_INSET + 1, top: y + h - WALL_INSET - WALL_T, width: w - 2 * WALL_INSET - 2, height: WALL_T };
  if (side === "left") return { left: x + WALL_INSET, top: y + WALL_INSET + 1, width: WALL_T, height: h - 2 * WALL_INSET - 2 };
  return { left: x + w - WALL_INSET - WALL_T, top: y + WALL_INSET + 1, width: WALL_T, height: h - 2 * WALL_INSET - 2 };
}

function Wall({ w, v, players }) {
  const b = box(w.side, v);
  const vertical = w.side === "left" || w.side === "right";
  const label = wallText(w, players);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid={`sea-wall-${w.square}-${w.side}`}
      data-owner={w.owner}
      data-sabotaged={w.sabotaged ? "true" : undefined}
      className="absolute"
      style={{
        ...b,
        boxSizing: "border-box",
        background: vertical
          ? "repeating-linear-gradient(180deg, #8E8A80 0 9px, #4E4B45 9px 10px)"
          : "repeating-linear-gradient(90deg, #8E8A80 0 9px, #4E4B45 9px 10px)",
        border: "1.5px solid #34322E",
        borderRadius: 1.6,
        // Raised strip: lit top edge, owner-colour ring, a drop shadow to the lower right.
        boxShadow: `inset 0 2px 0 0 rgba(217,212,199,.9), 0 0 0 1px ${PLAYER_COLORS[w.owner] || "#fff"}, 1.5px 2px 0 1px rgba(30,34,32,.38)`,
        pointerEvents: "auto",
      }}
    >
      {w.sabotaged && (
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 overflow-visible" aria-hidden="true">
          <line data-testid={`sea-wall-sabotage-${w.square}-${w.side}`} x1={vertical ? 0 : 3} y1={vertical ? 97 : 100} x2={vertical ? 100 : 97} y2={vertical ? 3 : 0} stroke="var(--danger)" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
    </span>
  );
}

function EdgePicker({ cell, options, v, onPick }) {
  const { left: x, top: y, width: s, height: sh } = v;
  const blocked = options.filter((o) => !o.ok);
  return (
    <>
      {options.map((o) => {
        const b = box(o.side, v);
        const grow = 3;
        const st = { left: b.left - grow, top: b.top - grow, width: b.width + 2 * grow, height: b.height + 2 * grow };
        return (
          <button
            key={o.side}
            type="button"
            data-testid={`edge-pick-${cell}-${o.side}`}
            data-ok={o.ok ? "true" : "false"}
            disabled={!o.ok}
            title={o.ok ? `${SIDE_LABEL[o.side]} edge` : o.reason}
            aria-label={o.ok ? `Build on the ${o.side} edge` : `${SIDE_LABEL[o.side]} edge: ${o.reason}`}
            onClick={(e) => {
              e.stopPropagation();
              if (o.ok) onPick(o.side);
            }}
            className={`absolute rounded-sm ${o.ok ? "edge-pick--ok" : ""}`}
            style={{
              ...st,
              pointerEvents: "auto",
              cursor: o.ok ? "pointer" : "not-allowed",
              border: o.ok ? "2px dashed var(--sea)" : "2px dashed #A7A196",
              background: o.ok ? "rgba(36,73,90,.16)" : "rgba(120,116,108,.28)",
            }}
          />
        );
      })}
      {blocked.length > 0 && (
        <span
          data-testid={`edge-pick-reasons-${cell}`}
          className="absolute pointer-events-none"
          style={{ left: x + 12, top: y + sh / 2 - 8 * blocked.length, width: s - 24, fontSize: 9.5, lineHeight: "12px", color: "#34322E", background: "rgba(251,250,247,.92)", borderRadius: 4, padding: "2px 4px", textAlign: "center" }}
        >
          {blocked.map((o) => (
            <span key={o.side} className="block">{`${SIDE_LABEL[o.side]}: ${o.reason}`}</span>
          ))}
        </span>
      )}
    </>
  );
}

// Layer over every block (same origin as the board grid): every wall, plus the edge picker when
// asked. 3D BOARD: walls and the picker sit on the square's top face (the part no taller
// neighbour covers), so they lift with it.
export default function EdgeWalls({ walls, geom, players = [], picker = null }) {
  return (
    <div data-testid="edge-walls" className="absolute inset-0 pointer-events-none" style={{ zIndex: picker ? LAYER.picker : LAYER.walls }}>
      {Object.values(walls || {}).map((w) => {
        if (!w?.square) return null;
        return <Wall key={w.edge} w={w} v={geom.vis(w.square)} players={players} />;
      })}
      {picker && (
        <div className="absolute inset-0" style={{ zIndex: 2 }}>
          <EdgePicker cell={picker.cell} options={picker.options} v={geom.vis(picker.cell)} onPick={picker.onPick} />
        </div>
      )}
    </div>
  );
}
